// src/app/public/survey/public-survey.component.ts
// ════════════════════════════════════════════════════════════════════════════
// DE-7 — the public survey page.  Route: /s/:slug   (outside the authed shell)
//
// This is the only screen in the product a stranger will ever see. It has to work on a cheap phone,
// on a bad connection, in a language the person actually reads, and it has to be honest about what
// happens to their answers.
//
// The language switcher is not decoration here — it is the entire reason DE-6 and DE-7 ship
// together. A feedback form in a village that only speaks Telugu, rendered in English, collects
// nothing. That is not a translation gap; it is a survey that does not work.
// ════════════════════════════════════════════════════════════════════════════
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, Subscription } from 'rxjs';

import { apiUrl } from '../../../environments/environment';
import { FormioComponent } from 'angular-formio';

import { I18nService } from '../../services/i18n.service';

@Component({
  selector: 'app-public-survey',
  templateUrl: './public-survey.component.html',
  styleUrls: ['./public-survey.component.scss'],
})
export class PublicSurveyComponent implements OnInit, OnDestroy {

  private api = apiUrl + '/api/public/survey/';
  private langSub?: Subscription;

  slug = '';
  token = '';

  loading = true;
  submitting = false;

  /** Set when the survey cannot be answered. Carries the org's own wording, not an error code. */
  closedMessage = '';

  survey: any = null;

  /** Set when the logo 404s, so the broken-image box never reaches a respondent. */
  logoFailed = false;

  /** The same, for the event's logo — a separate file that can go missing on its own. */
  eventLogoFailed = false;

  /**
   * The languages THIS template is actually translated into.
   *
   * The switcher's own list comes from the app, so without this it offered
   * languages the form could not speak — the chrome changed and every question
   * stayed in English. `i18n` is keyed by language and only carries the ones
   * with real translations, so it is the honest source.
   */
  get translatedLangs(): string[] {
    return Object.keys((this.survey && this.survey.i18n) || {});
  }

  formioForm: any = null;

  /**
   * Set here as well as on load, because Form.io reads its options once, in its own ngOnInit,
   * and that runs before the survey has been fetched. An empty object at that moment means it
   * takes its defaults — and its default is to show an alert panel.
   *
   * `disableAlerts` and `noAlerts` are two different switches for two different panels, which is
   * not obvious and cost an afternoon:
   *   - `noAlerts`      — Form.io's own alert, drawn inside the rendered form.
   *   - `disableAlerts` — the Angular wrapper's `<formio-alerts>` block above the form.
   * Only the second one was missing, so pressing Submit on an empty form listed every error twice:
   * once in a red block at the top, once under the field it belonged to.
   *
   * The list at the top is the one worth losing. It repeats what the fields already say, and it
   * says it far away from the box that needs fixing — on a phone, usually off the screen.
   */
  formioOptions: any = { noAlerts: true, disableAlerts: true };
  submissionData: any = { data: {} };

  consent = false;
  contact = '';

  /**
   * Verdentum's Terms of Use and Privacy Policy (DE-8).
   *
   * Separate from `consent`, and required on every public survey regardless of
   * mode. The two are not interchangeable: `consent` is the ORGANISATION's
   * promise about what it will do with the person's details, and it only appears
   * when the org has written one. This is the platform's own terms, and the
   * person filling the form has no account and has never been shown them
   * anywhere else.
   */
  terms = false;

  /**
   * The honeypot. It is rendered off-screen and no human ever sees it, let alone fills it. A bot
   * fills every input it finds. If this arrives non-empty the backend answers 200 OK and silently
   * drops the response — telling a bot it was caught only teaches it to try again differently.
   */
  hp = '';

  done = false;
  thanksText = '';

  /**
   * Shown only after somebody has actually pressed Submit.
   *
   * Nothing is complained about while the form is still being filled in — a page
   * that tells a person off for not having finished yet is a page they stop
   * reading.
   */
  validationError = '';

  gps: { lat: number; lng: number; accuracy: number } | null = null;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    public i18n: I18nService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.slug = this.route.snapshot.paramMap.get('slug') || '';
    this.token = this.route.snapshot.queryParamMap.get('t') || '';
    await this.load();
    // R14: follow the language AFTER it has changed. The switcher's (click) fired before
    // I18nService finished fetching the pack, so the form was re-told the OLD language.
    this.langSub = this.i18n.changed.subscribe(() => this.onLanguage());
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  private post(path: string, body: any) {
    return firstValueFrom(
      this.http.post(this.api + path, body, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      })
    );
  }

  async load(): Promise<void> {
    this.loading = true;
    this.closedMessage = '';
    try {
      const res: any = await this.post('get', { slug: this.slug, token: this.token || undefined });

      if (Number(res?.status) !== 1) {
        // Every refusal is a sentence, not a code. This person is not a developer and did nothing
        // wrong — the survey is simply closed, or full, or not for them.
        this.closedMessage = res?.msg || 'This survey is not available.';
        return;
      }

      this.survey = res.data;
      this.thanksText = res.data?.thanks || '';

      // Form.io renders the form. `i18n` is the label map the backend built from the template's
      // translations, so the form itself speaks the chosen language — not just the chrome around it.
      this.formioForm = res.data.formio_schema;
      this.formioOptions = {
        noAlerts: true,
        disableAlerts: true,
        i18n: res.data.i18n || {},
        language: this.i18n.lang,
      };

      if (res.data.capture_gps) this.captureGps();
    } catch {
      this.closedMessage = 'Could not open this survey. Please check your connection and try again.';
    } finally {
      this.loading = false;
    }
  }

  /**
   * The Form.io form has to be told about a language change, not just the page around it.
   *
   * Tester round 2, R14 — replacing `formioOptions` alone did nothing: angular-formio reads
   * [options] once, when the form is first built. The live form instance has to be given the
   * translations and switched, exactly as the in-app Forms screen does (applyFormLanguage).
   */
  async onLanguage(): Promise<void> {
    const lang = this.i18n.lang;
    this.formioOptions = { ...this.formioOptions, language: lang };
    const form: any = (this.formioComp as any)?.formio;
    if (!form) return;
    try {
      const packs = (this.survey && this.survey.i18n) || {};
      for (const [code, labels] of Object.entries(packs)) {
        if (code !== 'en' && labels && Object.keys(labels as any).length) form.addLanguage(code, labels);
      }
      form.language = lang;
    } catch (e) {
      console.warn('[public-survey] language switch failed', e);
    }
  }

  private captureGps(): void {
    if (!navigator.geolocation) return;
    // A best-effort single fix. This is a stranger on a public link — we are not going to hold their
    // phone's GPS open for 30 seconds (DE-5's watch strategy) for a feedback form, and we do not ask
    // permission twice if they refuse.
    navigator.geolocation.getCurrentPosition(
      (p) => {
        this.gps = {
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracy: Math.round(p.coords.accuracy),
        };
      },
      () => { /* no GPS, no problem — the response is still worth having */ },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  }

  get needsConsent(): boolean {
    return !!this.survey && this.survey.mode !== 'anonymous' && !!this.survey.consent_text;
  }

  /**
   * What is stopping this submission, if anything. Null when it is good to go.
   *
   * Deliberately NOT wired to `[disabled]`. A greyed-out button with a sentence
   * underneath tells somebody they are stuck without telling them why they are
   * stuck — they have to connect two things on opposite sides of the screen, and
   * on a phone the message is often below the fold. Pressing the button and
   * being told is one step, and it is the step they were already taking.
   */
  private get blocker(): string | null {
    const needTerms = this.needsTerms && !this.terms;
    const needConsent = this.needsConsent && !this.consent;

    if (needConsent && needTerms) {
      return 'Please accept the consent statement and Verdentum\'s terms to continue.';
    }
    if (needConsent) return 'Please accept the consent statement to continue.';
    if (needTerms) return 'Please accept Verdentum\'s Terms of Use and Privacy Policy to continue.';
    return null;
  }

  /**
   * Whether this survey shows the platform terms at all.
   *
   * The org can switch the box off per survey. The server decides it from the
   * same row, so a hidden box can never become a submission the server rejects.
   */
  get needsTerms(): boolean {
    return !!this.survey?.show_terms;
  }

  /** The button is live unless a submission is already in flight. */
  get canSubmit(): boolean {
    return !this.submitting;
  }

  /**
   * The rendered Form.io form, so the page can ask it to validate itself.
   *
   * The page draws its OWN Submit button rather than letting Form.io draw one,
   * because the consent and terms boxes have to sit between the questions and
   * the button. That is the right layout and the wrong wiring: pressing our
   * button used to call send() directly, so Form.io's `(submit)` never fired and
   * its validation never ran. A template can mark Name, Company and Email
   * required, show the asterisk next to each of them, and still be submitted
   * completely blank.
   *
   * It was not theoretical. On 2026-09-10 four responses to the Founders Survey
   * were stored on production with every single field empty.
   *
   * Nothing is lost by asking Form.io to run the submit it would have run for
   * its own button: it validates, marks the offending fields, scrolls to the
   * first one, and only then emits `submit` -- which lands in onSubmit() below.
   */
  @ViewChild(FormioComponent) private formioComp?: FormioComponent;

  /**
   * The page's Submit button.
   *
   * Hands the press to Form.io. On a valid form Form.io emits `submit`, which
   * reaches onSubmit() and then send(). On an invalid one it rejects, having
   * already told the person which fields it wants -- so there is nothing to add
   * here, and nothing to send.
   */
  async submitPressed(): Promise<void> {
    if (this.submitting) return;

    // Set before validating, not after, so one press can report both kinds of
    // problem at once: the empty question AND the unticked terms box. Otherwise
    // a person fixes the fields, presses again, and only then learns about the
    // checkbox.
    this.validationError = this.blocker || '';

    const webform: any = (this.formioComp as any)?.formio;
    if (!webform) {
      // The form never rendered -- a schema that failed to load, say. There is
      // nothing to validate against here, and the server checks the submission
      // regardless, so this must not become a dead button.
      await this.send();
      return;
    }

    try {
      await webform.submit();
    } catch {
      // Invalid. Form.io has marked the fields and moved the page to the first
      // one; a second sentence from us would only be noise.
    }
  }

  /**
   * Form.io's own submit event -- reached only after its validation passed.
   *
   * `sub` is Form.io's copy of the submission, which is what should be sent: it
   * is the state the form actually validated.
   */
  async onSubmit(sub: any): Promise<void> {
    this.submissionData = sub || { data: {} };
    await this.send();
  }

  async send(): Promise<void> {
    if (this.submitting) return;

    // Checked on the press, not before it. The backend refuses these too — this
    // only saves the person a round trip and tells them which box it was.
    this.validationError = this.blocker || '';
    if (this.validationError) return;

    this.submitting = true;
    try {
      const res: any = await this.post('submit', {
        slug: this.slug,
        token: this.token || undefined,
        submission: this.submissionData,
        consent: this.needsConsent ? this.consent : undefined,
        terms: this.needsTerms ? this.terms : undefined,
        contact: this.survey?.mode === 'identified' ? (this.contact || null) : undefined,
        lang: this.i18n.lang,
        hp: this.hp,                        // the honeypot — always empty for a human
        gps_lat: this.gps?.lat,
        gps_lng: this.gps?.lng,
        gps_accuracy: this.gps?.accuracy,
      });

      if (Number(res?.status) !== 1) {
        this.closedMessage = res?.msg || 'Could not submit your response.';
        return;
      }

      this.thanksText = res?.data?.thanks || this.thanksText;
      this.done = true;
      window.scrollTo(0, 0);
    } catch {
      this.closedMessage = 'Could not submit your response. Please check your connection and try again.';
    } finally {
      this.submitting = false;
    }
  }
}
